import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import toast from "react-hot-toast";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import { getSessionStorage } from "../../../helpers/storage";
import {
  CurrentProviderData,
  IntakeSheetSession,
  TriggerPointConsent as TriggerPointConsentKeys,
  RoleType,
} from "../../../Constant";
import { GetCaseFormListData, SaveCaseFormData } from "../Service";
import { GragIntakeSheetData } from "../IntakeSheetUtils";
import SignaturePad from "../../../Component/shared/signaturePad";
import "./TriggerPointInjectionConsent.css";
import { usePatientData } from "../../../commonFunction/storage/Patientdatacontext";


const TriggerPointConSent = forwardRef(
  function TriggerPointConSent(props, ref) {
    const {
      providerOptions = [],
      sessionData = {},
      signatureMiss,
      copySignature,
    } = props;

    const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
    const providerData = getSessionStorage(CurrentProviderData) || [];
    const roleName = sessionData?.Role?.Name?.trim().toLowerCase() ?? "";
    const isFrontDesk =
      roleName === "frontdesk" ||
      roleName === "front desk" ||
      roleName === "admin";

    /* ── state ── */
    const [loading, setLoading] = useState(false);
    const [CaseFormDataId, setCaseFormDataId] = useState("");
    const [collectionOfTC, setCollectionOfTC] = useState([]);
    const [BeforeParsingTC, setBeforeParsingTC] = useState([]);

    // form fields
    const [patientName, setPatientName] = useState("");
    const [providerName, setProviderName] = useState("");
    const [providerAddress, setProviderAddress] = useState("");

    // dates
    const [patientDOS, setPatientDOS] = useState(new Date());
    const [physicianDOS, setPhysicianDOS] = useState(new Date());
    const [formSubmissionDate, setFormSubmissionDate] = useState(new Date());

    // provider select
    const [selectedProvider, setSelectedProvider] = useState(
      providerOptions[0] || null,
    );
    const [isSelectedProvider, setIsSelectedProvider] = useState(false);

    // signatures
    const [patientSignImg, setPatientSignImg] = useState("");
    const [providerSignImg, setProviderSignImg] = useState("");
    const [sigPadOpen, setSigPadOpen] = useState(false);
    const [signRole, setSignRole] = useState("");
    const [procedures, setProcedures] = useState([]);

    const { patientData, fetchPatientData } = usePatientData();


    useImperativeHandle(
      ref,
      () => ({
        save: () => saveFunction(),
        isPending: () => !patientSignImg,
        getSignature: () => patientSignImg || "",
        applySignature: (sig) => {
          if (!sig) return;
          setPatientSignImg(sig);
          setPatientDOS(new Date());
        },
      }),
      [patientSignImg, procedures,patientDOS,formSubmissionDate],
    );

    useEffect(() => {
      props?.onReady?.("trigger_point_consent");
    }, [patientSignImg]);

    useEffect(() => {
      callApi();
    }, [patientData]);

    /* ══════════════════════════════════════════════════
     FETCH
  ══════════════════════════════════════════════════ */
    const handleGetCaseFormData = async () => {
      try {
        const requestBody = {
          CaseFormDataId: null,
          CaseId: tokenData.caseId,
          CompanyId: tokenData.companyId,
          DoctorId: "ALL",
          EntityLevel: 0,
          FormId: 8, // ← update to correct FormId for Trigger Point
          FormName: "Trigger Point Injection Consent",
          FormType: null,
          IsFormCompleted: false,
          JsonData: null,
          TemplateName: null,
          UserId: tokenData?.userId,
          officeId: tokenData?.providerId,
        };
        const response = await GetCaseFormListData(requestBody);
        if (response?.HasException) {
          toast.error(
            `Something went wrong. ${response?.MessageText}. ${response?.Exception}`,
          );
          return {};
        }
        return response?.Data[0] || {};
      } catch (error) {
        toast.error(error.message);
        return {};
      }
    };

    const callApi = async () => {
      setLoading(true);
      const formData = await handleGetCaseFormData();
    const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);

      if (!formData?.JsonData) {
        console.error("No JsonData returned from GetCaseFormListData");
        setLoading(false);
        return;
      }

      const tcData = JSON.parse(formData.JsonData);
      const collection = GragIntakeSheetData(tcData);

      setCollectionOfTC(collection);
      setBeforeParsingTC(tcData);
      setCaseFormDataId(formData.CaseFormDataId);

      bindValueToControl(collection, patientInfo);
      setLoading(false);
    };


    /* ══════════════════════════════════════════════════
     BIND VALUES
  ══════════════════════════════════════════════════ */
    const bindValueToControl = (collection, patientInfo) => {
      if (!patientInfo) return;

      const getValue = (key) => {
        const found = collection.find((x) => x.Name === key);
        return found ? found.Value : "";
      };

      const parseDateSafe = (raw) => {
  if (!raw || raw === "01/01/1900") return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

      const normalise = (raw) => {
        if (!raw) return "";
        return raw.includes(IntakeSheetSession.dataBlock)
          ? raw
          : IntakeSheetSession.dataBlock + raw;
      };

      const procedureVal = getValue(TriggerPointConsentKeys.Procedures);
      if (procedureVal) {
        const lines = procedureVal
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        setProcedures(lines);
      }

      // Patient name from patientInfo
      const fullName = [
        patientInfo.patientFirstName,
        patientInfo.patientMiddleName,
        patientInfo.patientLastName,
      ]
        .filter(Boolean)
        .join(" ");
      setPatientName(fullName);

      // Dates
      setPatientDOS(
        parseDateSafe(getValue(TriggerPointConsentKeys.PatientSignDate)),
      );
      const physicianDate = parseDateSafe(
  getValue(TriggerPointConsentKeys.PhysicianSignDate)
);

setPhysicianDOS(physicianDate);
      setFormSubmissionDate(
        parseDateSafe(getValue(TriggerPointConsentKeys.FormSubDate)),
      );

      // Signatures
      const savedSig = copySignature
        ? sessionStorage.getItem("sharedPatientSignature")
        : null;
      const patientSig =
        savedSig || normalise(getValue(TriggerPointConsentKeys.PatientSign));
      setPatientSignImg(patientSig);
      setProviderSignImg(
        normalise(getValue(TriggerPointConsentKeys.PhysicianSign)),
      );
      if (!patientSig) signatureMiss?.(true);

      // Provider
      if (isFrontDesk) {
        const savedProvName = getValue(TriggerPointConsentKeys.ProviderName);
        const provider =
          providerOptions.find((x) => x.label === savedProvName) ||
          providerOptions[0] ||
          null;
        const forAddr = providerData.find(
          (x) => x.ProviderId === provider?.value,
        );
        const addr = forAddr
          ? [
              forAddr.Address,
              forAddr.City,
              forAddr.Street,
              forAddr.State,
              forAddr.Zip,
            ]
              .filter(Boolean)
              .join(", ")
          : "";
        setSelectedProvider(provider);
        setProviderName(provider?.label || "");
        setProviderAddress(addr);
      } else {
        const prov = providerData[0] || {};
        setProviderName(
          getValue(TriggerPointConsentKeys.ProviderName) ||
            prov.ProviderName ||
            tokenData?.providerName ||
            "",
        );
        setProviderAddress(
          getValue(TriggerPointConsentKeys.ProviderAddress) ||
            [prov.Address, prov.City, prov.Street, prov.State, prov.Zip]
              .filter(Boolean)
              .join(", "),
        );
      }

      setLoading(false);
    };

    /* ══════════════════════════════════════════════════
     UPDATE COLLECTION
  ══════════════════════════════════════════════════ */
    const updateCollection = (collection) => {
      try {
        const updated = [...collection];
        const set = (key, value) => {
          const item = updated.find((x) => x.Name === key);
          if (item) item.Value = value;
        };
        const fmt = (d) =>
          d == null || d === ""
            ? ""
            : moment(d).format("MM-DD-YYYY").split("-").join("/");

        const resolvedProvName = isFrontDesk
          ? providerOptions.length === 1
            ? providerOptions[0]?.label || ""
            : selectedProvider?.label || ""
          : providerData[0]?.ProviderName || providerName;

        const resolvedProvAddress = isFrontDesk
          ? providerAddress
          : providerData[0]
            ? [
                providerData[0].Address,
                providerData[0].City,
                providerData[0].Street,
                providerData[0].State,
                providerData[0].Zip,
              ]
                .filter(Boolean)
                .join(", ")
            : providerAddress;

        set(TriggerPointConsentKeys.PatientName, patientName);
        set(TriggerPointConsentKeys.ProviderName, resolvedProvName);
        set(TriggerPointConsentKeys.ProviderAddress, resolvedProvAddress);
        set(TriggerPointConsentKeys.PatientSign, patientSignImg);
        set(TriggerPointConsentKeys.PhysicianSign, providerSignImg);
        set(TriggerPointConsentKeys.PatientSignDate, fmt(patientDOS));
        set(TriggerPointConsentKeys.PhysicianSignDate, fmt(physicianDOS));
        set(TriggerPointConsentKeys.FormSubDate, fmt(formSubmissionDate));
        set("Procedures", procedures.join("\n"));

        return updated;
      } catch (error) {
        console.error(error?.message);
      }
    };

    /* ══════════════════════════════════════════════════
     SAVE API
  ══════════════════════════════════════════════════ */
    const callSaveApi = async (jsonData) => {
      try {
        const doctorId = isFrontDesk
          ? (providerOptions.length === 1
              ? providerOptions[0]?.value
              : selectedProvider?.value) || ""
          : providerData[0]?.ProviderId || tokenData?.doctorId || "";

        const payload = {
          CaseFormDataId: CaseFormDataId,
          CaseId: tokenData.caseId,
          CompanyId: tokenData.companyId,
          DoctorId: doctorId,
          EntityLevel: 0,
          FormId: 8, // ← update to correct FormId for Trigger Point
          FormName: "Trigger Point Injection Consent",
          FormType: null,
          IsChanged: false,
          IsCopySign: false,
          IsFormCompleted: false,
          UserId: tokenData?.userId,
          officeId: tokenData?.providerId,
          JsonData: jsonData,
        };

        const response = await SaveCaseFormData(payload);

        if (response.HasException) {
          toast.error("Error while saving Trigger Point Injection Consent.");
          return false;
        }
        toast.success("Trigger Point Injection Consent saved successfully.");
        return true;
      } catch {
        toast.error("Error while saving Trigger Point Injection Consent.");
        return false;
      }
    };

    /* ══════════════════════════════════════════════════
     FORM JSON REQUEST
  ══════════════════════════════════════════════════ */
    const signFields = [
      TriggerPointConsentKeys.PatientSign,
      TriggerPointConsentKeys.PhysicianSign,
    ];

    const formJsonRequest = async (updatedCollection, BeforeParsingData) => {
      try {
        for (let i = 0; i < BeforeParsingData.notesdata.length; i++) {
          const filterDataArr = updatedCollection.filter(
            (x) =>
              x.RenderKeyName === BeforeParsingData.notesdata[i].RenderKeyName,
          );
          if (filterDataArr.length > 0) {
            for (
              let j = 0;
              j < BeforeParsingData.notesdata[i].ApplicationValue.length;
              j++
            ) {
              if (
                BeforeParsingData.notesdata[i].ApplicationValue[j].Name ===
                filterDataArr[j]?.Name
              ) {
                BeforeParsingData.notesdata[i].ApplicationValue[j].Type =
                  filterDataArr[j].Type;
                BeforeParsingData.notesdata[i].ApplicationValue[j].isRender =
                  filterDataArr[j].isRender;

                const isSign = signFields.includes(
                  BeforeParsingData.notesdata[i].ApplicationValue[j].Name,
                );

                if (isSign) {
                  const val = filterDataArr[j].Value;
                  if (val) {
                    if (val.includes(";")) {
                      const block = val.split(";");
                      BeforeParsingData.notesdata[i].ApplicationValue[j].Value =
                        block[1].split(",")[1];
                    } else {
                      BeforeParsingData.notesdata[i].ApplicationValue[j].Value =
                        val;
                    }
                  } else {
                    BeforeParsingData.notesdata[i].ApplicationValue[j].Value =
                      "";
                  }
                } else {
                  BeforeParsingData.notesdata[i].ApplicationValue[j].Value =
                    filterDataArr[j].Value;
                }
              }
            }
          }
        }
        const result = await callSaveApi(JSON.stringify(BeforeParsingData));
        setCollectionOfTC(updatedCollection);
        setBeforeParsingTC(BeforeParsingData);
        return result;
      } catch (error) {
        console.error(error.message);
      }
    };

    /* ══════════════════════════════════════════════════
     SAVE FUNCTION
  ══════════════════════════════════════════════════ */
    const saveFunction = async () => {
      try {
        setLoading(true);
        const updatedCollection = updateCollection(collectionOfTC);
        const result = await formJsonRequest(
          updatedCollection,
          BeforeParsingTC,
        );
        setLoading(false);
        return result;
      } catch (error) {
        console.error(error?.message);
      }
    };

    const clickSign = (role) => {
      setSignRole(role);
      setSigPadOpen(true);
    };

    const signUpdatedCall = (base64Img) => {
      const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
        ? base64Img.replace(
            IntakeSheetSession.dataBlock1,
            IntakeSheetSession.dataBlock,
          )
        : base64Img;

      if (signRole === RoleType.Patient) {
        setPatientSignImg(newBase);
        setPatientDOS(new Date());
        sessionStorage.setItem("sharedPatientSignature", newBase);
      }
      if (signRole === RoleType.Doctor) {
        setProviderSignImg(newBase);
        setPhysicianDOS(new Date());
      }
    };

    const clearSignCall = () => {
      if (signRole === RoleType.Patient) {
        setPatientSignImg("");
        signatureMiss?.(true);
      }
      if (signRole === RoleType.Doctor) {
        setProviderSignImg("");
      }
    };

    /* ══════════════════════════════════════════════════
     PROVIDER CHANGE
  ══════════════════════════════════════════════════ */
    const onProviderChange = (prov) => {
      if (!prov) return;
      const forAddr = providerData.find((x) => x.ProviderId === prov.value);
      const addr = forAddr
        ? [
            forAddr.Address,
            forAddr.City,
            forAddr.Street,
            forAddr.State,
            forAddr.Zip,
          ]
            .filter(Boolean)
            .join(", ")
        : "";
      setSelectedProvider(prov);
      setProviderName(prov.label || "");
      setProviderAddress(addr);
      setIsSelectedProvider(false);
    };

    const ProviderField = () =>
      providerOptions.length === 1 ? (
        <input
          className="tpc__input"
          type="text"
          value={providerOptions[0]?.label || ""}
          readOnly
        />
      ) : (
        <Select
          options={providerOptions}
          onChange={onProviderChange}
          value={selectedProvider}
          getOptionLabel={(e) => e?.label}
          getOptionValue={(e) => e?.value}
          tabSelectsValue={false}
          classNamePrefix={`react-select${isSelectedProvider ? " react-select--error" : ""}`}
          placeholder="Select Provider"
        />
      );

    const displayProvider = isFrontDesk
      ? providerOptions.length === 1
        ? providerOptions[0]?.label || ""
        : providerName
      : providerData[0]?.ProviderName || providerName;

    /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
    return (
      <>
        {loading ? (
          <SpinnerLoader message="Loading form data…" />
        ) : (
          <div className="tpc__wrapper">
            {/* ── Title ── */}
            <div className="tpc__title-block">
              <div className="tpc__title-main">
                CONSENT FOR TRIGGER POINT INJECTIONS
              </div>
            </div>

            {/* ── Intro paragraph ── */}
            <div className="tpc__legal-para">
              The trigger point injection involves placing a small needle in or
              around the painful area to instill a numbing medication or other
              type of solution. The nature and purpose of this procedure has
              been explained. I do understand that this is considered an
              elective procedure. Any questions regarding the procedure have
              been answered to my satisfaction.
            </div>

            {/* ── Side effects ── */}
            <div className="tpc__section-label">
              <b>
                Possible side effects associated with this procedure include:
              </b>
            </div>
            <div className="tpc__side-effects">
              {[
                "Reactions to medication including minor or major allergic reaction.",
                "Vasovagal reactions (fainting) could occur during or after the procedure with possible heart and blood pressure problems.",
                "Increased pain temporarily.",
                "Bleeding and bruising.",
                "Infection either around the injection site or deep in a body cavity, bone, joint or spine.",
                "Other extremely rare complications might include collapsed lung, temporary or permanent nerve impairment, seizures, stroke, paralysis, or death.",
              ].map((effect, i) => (
                <div key={i} className="tpc__side-effect-item">
                  <span className="tpc__effect-num">{i + 1}.</span>
                  <span>{effect}</span>
                </div>
              ))}
            </div>

            {/* ── Provider name ── */}
            <div className="tpc__field-row">
              <span className="tpc__label">
                <b>Name of Provider:</b>
              </span>
              <div className="tpc__field-control">
                {isFrontDesk ? (
                  <ProviderField />
                ) : (
                  <input
                    className="tpc__input tpc__input--grow"
                    type="text"
                    value={displayProvider}
                    disabled
                  />
                )}
              </div>
            </div>

            {/* ── Provider address ── */}
            <div className="tpc__field-row tpc__field-row--top">
              <span className="tpc__label">
                <b>Address of Provider:</b>
              </span>
              <textarea
                className="tpc__textarea"
                value={providerAddress}
                disabled
                rows={3}
              />
            </div>

            {/* ── Name of Procedure ── */}
            <div className="tpc__field-row">
              <span className="tpc__label">
                <b>Name of Procedure:</b>
              </span>
              <div className="tpc__procedures-list">
                {procedures.length > 0 ? (
                  procedures.map((proc, i) => (
                    <div key={i} className="tpc__procedure-tag">
                      {proc}
                    </div>
                  ))
                ) : (
                  <span className="tpc__no-procedure">
                    No procedures selected
                  </span>
                )}
              </div>
            </div>

            {/* ── Patient name ── */}
            <div className="tpc__field-row">
              <span className="tpc__label">
                <b>Name:</b>
              </span>
              <input
                className="tpc__input tpc__input--grow"
                type="text"
                value={patientName}
                disabled
              />
            </div>

            {/* ── Signature grid ── */}
            <div className="tpc__sig-grid">
              {/* Patient */}
              <div className="tpc__sig-col">
                <span className="tpc__label">
                  <b>Signature :</b>
                </span>
                <div
                  className={`tpc__sign-box${!patientSignImg ? " tpc__sign-box--empty" : ""}`}
                  onClick={() => clickSign(RoleType.Patient)}
                >
                  {patientSignImg ? (
                    <img
                      src={patientSignImg}
                      alt="Patient signature"
                      className="tpc__sign-img"
                    />
                  ) : (
                    <span className="tpc__sign-hint">Click to sign</span>
                  )}
                </div>
                <DatePicker
                  selected={patientDOS}
                  onChange={(d) => setPatientDOS(d)}
                  dateFormat="MM/dd/yyyy"
                  className="tpc__datepicker"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                />
                <span className="tpc__date-label">Date of Signature</span>
              </div>

              {/* Physician */}
              <div className="tpc__sig-col">
                <span className="tpc__label">
                  <b>Signature of Physician/PA/NP:</b>
                </span>
                <div
                  className="tpc__sign-box tpc__sign-box--disabled"
                >
                  {providerSignImg && (
                    <img
                      src={providerSignImg}
                      alt="Physician signature"
                      className="tpc__sign-img"
                    />
                  )}
                </div>
                <DatePicker
                  selected={physicianDOS}
                  onChange={(d) => setPhysicianDOS(d)}
                  dateFormat="MM/dd/yyyy"
                  className="tpc__datepicker"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  disabled
                />
                <span className="tpc__date-label">Date of Signature</span>
              </div>
            </div>

            {/* ── Form submission date ── */}
            <div className="tpc__submission-row">
              <span className="tpc__label">
                <b>
                  Date of Form Submission{" "}
                  <span className="tpc__auto-note">(Set Automatically)</span>:
                </b>
              </span>
              <DatePicker
                selected={formSubmissionDate}
                onChange={(d) => setFormSubmissionDate(d)}
                dateFormat="MM/dd/yyyy"
                className="tpc__datepicker"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </div>

            {/* ── Signature Pad ── */}
            <SignaturePad
              isOpen={sigPadOpen}
              title={
                signRole === RoleType.Patient
                  ? "Patient Signature"
                  : "Physician Signature"
              }
              existingSign={
                signRole === RoleType.Patient ? patientSignImg : providerSignImg
              }
              onClose={() => setSigPadOpen(false)}
              onSave={(base64) => {
                signUpdatedCall(base64);
                setSigPadOpen(false);
              }}
              onClear={clearSignCall}
            />
          </div>
        )}
      </>
    );
  },
);

export default TriggerPointConSent;
